import { cn } from '@/lib/utils';
import * as React from 'react';
import { Pressable, StyleProp, View, ViewStyle } from 'react-native';
import { Text } from './text';

interface TabsContextProps {
  value: string;
  onValueChange: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextProps | undefined>(undefined);

function useTabsContext() {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs compound components must be used within a Tabs');
  }
  return context;
}

interface TabsProps {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  children: React.ReactNode;
  className?: string;
}

function Tabs({ value: controlledValue, onValueChange, defaultValue = '', children, className }: TabsProps) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setUncontrolledValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange]
  );

  return (
    <TabsContext.Provider value={{ value, onValueChange: handleValueChange }}>
      <View className={className}>{children}</View>
    </TabsContext.Provider>
  );
}

type TabsListProps = React.ComponentPropsWithoutRef<typeof View>;

const TabsList = React.forwardRef<React.ComponentRef<typeof View>, TabsListProps>(
  ({ className, ...props }, ref) => (
    <View
      ref={ref}
      className={cn(
        'flex-row items-start justify-start border-b border-border ps-5 gap-3',
        className
      )}
      {...props}
    />
  )
);
TabsList.displayName = 'TabsList';

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof Pressable> {
  value: string;
  children?: React.ReactNode;
  title?: string;
  textClassName?: string;
  activeTextClassName?: string;
  activeStyle?: StyleProp<ViewStyle>;
  inactiveStyle?: StyleProp<ViewStyle>;
  activeClassName?: string;
  inactiveClassName?: string;
}

const TabsTrigger = React.forwardRef<React.ComponentRef<typeof Pressable>, TabsTriggerProps>(
  ({ className, value, children, title, textClassName, activeTextClassName, activeStyle, inactiveStyle, activeClassName, inactiveClassName, ...props }, ref) => {
    const { value: activeValue, onValueChange } = useTabsContext();
    const isActive = activeValue === value;

    return (
      <Pressable
        ref={ref}
        role="tab"
        aria-selected={isActive}
        onPress={() => onValueChange(value)}
        className={cn(
          'flex-1 items-center justify-center px-3 py-2',
          isActive && !activeStyle && !activeClassName && 'border-b-4 border-primary',
          isActive ? activeClassName : inactiveClassName,
          className
        )}
        style={isActive ? activeStyle : inactiveStyle}
        {...props}
      >
        {children ?? (
          <Text
            className={cn(
              'text-base font-medium',
              isActive ? 'text-foreground' : 'text-muted-foreground',
              isActive && activeTextClassName,
              textClassName
            )}
          >
            {title}
          </Text>
        )}
      </Pressable>
    );
  }
);
TabsTrigger.displayName = 'TabsTrigger';

interface TabsContentProps extends React.ComponentPropsWithoutRef<typeof View> {
  value: string;
}

const TabsContent = React.forwardRef<React.ElementRef<typeof View>, TabsContentProps>(
  ({ className, value, ...props }, ref) => {
    const { value: activeValue } = useTabsContext();

    if (value !== activeValue) {
      return null;
    }

    return (
      <View
        ref={ref}
        role="tabpanel"
        className={cn('mt-2', className)}
        {...props}
      />
    );
  }
);
TabsContent.displayName = 'TabsContent';

export { Tabs, TabsContent, TabsList, TabsTrigger };
export type { TabsContentProps, TabsListProps, TabsProps, TabsTriggerProps };

